<script setup lang="ts">
import type { CollectionFieldPath, CollectionFieldPathCollection, CollectionFieldPathField, GlobalCollectionId } from '#layers/data-model/public';
import { computed, ref } from 'vue';
import { useAppConfig } from '#app/config';
import { useAuthorization } from '#layers/access/public';
import { CollectionFieldPathSelect, mayHaveHiddenCollectionFieldPath, toCollectionFieldPathKey, useCollection } from '#layers/data-model/public';
import { useOutputGate } from '#layers/listing/composables/use-output-gate';
import { tableColumnFieldPathOptions } from './table-column-field-path';

const {
	collectionId,
	visibleColumnPaths,
} = defineProps<{
	collectionId: GlobalCollectionId;
	visibleColumnPaths: CollectionFieldPath[];
}>();

const emit = defineEmits<{
	select: [fieldPath: CollectionFieldPath];
}>();

const appConfig = useAppConfig();
const authorization = useAuthorization();

const tooltipDisabled = ref(false);

const { collection } = useCollection(() => collectionId);
const outputGate = useOutputGate(collection);

const visibleColumnKeys = computed(() => new Set(visibleColumnPaths.map((fieldPath) => toCollectionFieldPathKey(fieldPath))));

const hasHiddenFields = computed(() => {
	if (!collection.value) return false;
	return mayHaveHiddenCollectionFieldPath(
		collection.value,
		isAddableFieldPath,
		{
			...tableColumnFieldPathOptions,
			includeField: canReadField,
		},
	);
});

function preventTooltip() {
	tooltipDisabled.value = true;
	setTimeout(() => tooltipDisabled.value = false, 2500);
}

function canReadField(collection: CollectionFieldPathCollection, field: CollectionFieldPathField) {
	return authorization.can({ collection, action: 'read', fields: [field.id] });
}

function isAddableFieldPath(fieldPath: CollectionFieldPath) {
	return outputGate.allowsPath(fieldPath) && !visibleColumnKeys.value.has(toCollectionFieldPathKey(fieldPath));
}

function handleSelect(fieldPath: CollectionFieldPath) {
	if (!isAddableFieldPath(fieldPath)) return;

	emit('select', fieldPath);
}
</script>

<template>
	<DTooltip v-if="hasHiddenFields" :text="$t('common.add_field')" :disabled="tooltipDisabled">
		<CollectionFieldPathSelect
			v-if="collection"
			:model-value="null"
			:collection
			:aria-label="$t('common.add_field')"
			v-bind="tableColumnFieldPathOptions"
			:include-field="canReadField"
			:filter-field-path="isAddableFieldPath"
			:search-placeholder="$t('common.search')"
			size="md"
			@update:open="preventTooltip()"
			@select="handleSelect"
		>
			<template #default="triggerProps">
				<DButton
					v-bind="triggerProps"
					variant="ghost"
					color="neutral"
					class="size-6 justify-center rounded-sm p-0"
					size="sm"
					:aria-label="$t('common.add_field')"
					:icon="appConfig.ui.icons.ellipsis"
				/>
			</template>
		</CollectionFieldPathSelect>
	</DTooltip>
</template>
